import { useEffect, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Progress } from "@/components/ui/progress";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import { Label } from "@/components/ui/label";
import {
  Loader2, Sparkles, Trash2, Plus, ArrowLeft, ArrowRight, Share2, X,
  SkipForward, ListChecks, User as UserIcon, Building2, Landmark, Trophy,
  Flag, HeartHandshake, Users as UsersIcon,
} from "lucide-react";
import { toast } from "sonner";
import { MedalCard } from "@/components/MedalCard";
import { AISourceSearchButton } from "@/components/AISourceSearchButton";
import { useAuth } from "@/lib/auth";
import { showError, normalizeError } from "@/lib/errors";

// V9.20 — Sprint 7B : Wizard 100 % remis à plat.
// Étape 0 : Type d'entité (Personne | Club | Fédération).
// Étape 1 (si Personne) : Multi-select rôles (athlete/coach/official/volunteer).
// Étape 2+ : parcours existant (identité, disciplines, sources, IA, revue).
// L'utilisateur peut cumuler les rôles : ils sont créés en parallèle côté backend.

const ENTITY_TYPES = [
  {
    key: "person",
    icon: UserIcon,
    title: "Une personne",
    subtitle: "Athlète, coach, officiel, bénévole — cumulables.",
  },
  {
    key: "club",
    icon: Building2,
    title: "Un club",
    subtitle: "Crée une page pour ton club et renseigne ses informations.",
  },
  {
    key: "federation",
    icon: Landmark,
    title: "Une fédération",
    subtitle: "Crée une page pour ta fédération et renseigne ses disciplines et affiliations.",
  },
];

const ROLE_OPTIONS = [
  {
    key: "athlete",
    icon: Trophy,
    label: "Athlète",
    desc: "Publier une fiche d'athlète : palmarès, résultats, disciplines.",
  },
  {
    key: "coach",
    icon: UsersIcon,
    label: "Coach / Encadrant",
    desc: "Encadrer des athlètes ; une fiche Coach dédiée est créée automatiquement.",
  },
  {
    key: "official",
    icon: Flag,
    label: "Officiel",
    desc: "Arbitre, juge, dirigeant sportif ; visible dans l'annuaire des officiels.",
  },
  {
    key: "volunteer",
    icon: HeartHandshake,
    label: "Bénévole",
    desc: "Participer à l'organisation ; référencé dans la communauté AITO.",
  },
];

const SPORT_LEVEL_OPTIONS = [
  { value: "", label: "Non spécifié" },
  { value: "loisir", label: "Loisir" },
  { value: "local", label: "Compétiteur local" },
  { value: "territorial", label: "Compétiteur territorial" },
  { value: "national", label: "Compétiteur national" },
  { value: "regional", label: "Compétiteur régional" },
  { value: "international", label: "Compétiteur international" },
  { value: "mondial", label: "Niveau mondial" },
  { value: "haut_niveau_confirme", label: "Haut niveau confirmé" },
  { value: "potentiel_haut_niveau", label: "Potentiel haut niveau" },
  { value: "a_completer", label: "À compléter" },
];

function WizardOtherDiscipline({ form, upd }) {
  const [open, setOpen] = useState(false);
  const [val, setVal] = useState("");
  const submit = () => {
    const v = val.trim();
    if (!v) { toast.error("Indiquez le nom de la discipline."); return; }
    const slug = v.toLowerCase().normalize("NFD").replace(/[\u0300-\u036f]/g, "").replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, "");
    if (form.disciplines.includes(slug)) { toast.error("Discipline déjà ajoutée."); return; }
    const list = [...form.disciplines, slug];
    upd({ disciplines: list, main_discipline: form.main_discipline || slug });
    setVal(""); setOpen(false);
    toast.success(`Discipline « ${v} » ajoutée.`);
  };
  if (!open) {
    return (
      <button type="button" data-testid="wizard-discipline-other" onClick={() => setOpen(true)}
        className="px-3 py-1.5 rounded-full text-sm border border-dashed hover:bg-muted">
        + Autre…
      </button>
    );
  }
  return (
    <div className="inline-flex items-center gap-1 border rounded-full pl-3 pr-1 py-1 bg-secondary/50">
      <Input autoFocus value={val} onChange={(e) => setVal(e.target.value)}
        onKeyDown={(e) => e.key === "Enter" && submit()}
        placeholder="Préciser la discipline"
        className="h-7 text-sm border-0 bg-transparent focus-visible:ring-0 px-1 min-w-[180px]" />
      <Button size="sm" className="h-7 px-2 bg-[hsl(var(--aito-lagoon))] text-black hover:bg-[hsl(var(--aito-lagoon-2))]" onClick={submit}>Ajouter</Button>
      <Button size="icon" variant="ghost" className="h-7 w-7" onClick={() => { setOpen(false); setVal(""); }}><X className="h-3.5 w-3.5" /></Button>
    </div>
  );
}

export default function CreateProfileWizard() {
  const { user } = useAuth();
  const nav = useNavigate();

  // Type d'entité + rôles multi-select
  const [entityType, setEntityType] = useState(null);            // "person" | "club" | "federation"
  const [roles, setRoles] = useState([]);                        // ["athlete", "coach", ...]

  // Steps
  //   0: Type d'entité
  //   1: Rôles (multi-select) — si Personne uniquement
  //   2: Identité
  //   3: Disciplines (uniquement si athlete OU coach)
  //   4: Sources (URLs — optionnel)
  //   5: IA (uniquement si URLs)
  //   6: Revue + publication
  //   7: Succès
  const [step, setStep] = useState(0);
  const [disciplines, setDisciplines] = useState([]);
  const [loadingAi, setLoadingAi] = useState(false);
  const [createdEntity, setCreatedEntity] = useState(null);

  const [form, setForm] = useState({
    full_name: user?.name || "",
    nickname: "",
    sex: "",
    commune: "",
    island: "",
    photo_url: "",
    disciplines: [],
    main_discipline: "",
    urls: [""],
    bio_short: "",
    bio_long: "",
    top_titles: [],
    highlights: [],
    journey_text: "",
    sport_level: "",
    period_start: null,
    period_end: null,
    proposed_status: null,
    proposed_level: null,
    proposed_level_justification: null,
    flash_recap: null,
    discipline_stats: [],
    articles: [],
    achievements: [],
    results: [],
    badges_suggested: [],
    clubs: [],
    federations: [],
    sources: [],
    confidence: null,
    consent_image_rights: false,
    consent_data_accuracy: false,
  });

  useEffect(() => { api.get("/disciplines").then((r) => setDisciplines(r.data.items || [])); }, []);

  const upd = (patch) => setForm((f) => ({ ...f, ...patch }));

  // Compétences dérivées
  const isPerson = entityType === "person";
  const isClubOrFed = entityType === "club" || entityType === "federation";
  const needsDisciplines = isPerson && (roles.includes("athlete") || roles.includes("coach"));

  // Progression dynamique selon la nature du flux
  const totalSteps = isPerson ? (needsDisciplines ? 7 : 6) : 5;

  const toggleRole = (key) => {
    setRoles((prev) => prev.includes(key) ? prev.filter((r) => r !== key) : [...prev, key]);
  };

  const toggleDiscipline = (slug) => {
    setForm((f) => {
      const list = f.disciplines.includes(slug) ? f.disciplines.filter((d) => d !== slug) : [...f.disciplines, slug];
      return { ...f, disciplines: list, main_discipline: f.main_discipline || list[0] || "" };
    });
  };

  const next = () => setStep((s) => s + 1);
  const back = () => setStep((s) => Math.max(0, s - 1));

  // V9.21 Hotfix — step de revue dépend du type d'entité :
  // person + disciplines = 6, person sans disciplines = 5, club/fédération = 4.
  const reviewStep = isPerson ? (needsDisciplines ? 6 : 5) : 4;

  const skipAi = () => {
    setStep(reviewStep);
    toast.info("Tu pourras tout compléter manuellement depuis ton espace.");
  };

  const runAi = async () => {
    const urls = form.urls.map((u) => u.trim()).filter(Boolean);
    if (urls.length === 0) { skipAi(); return; }
    setLoadingAi(true);
    try {
      const { data } = await api.post("/ai/autofill", {
        hint: `${form.full_name} | roles=${roles.join(",")} | disciplines: ${form.disciplines.join(", ")}`,
        urls,
      });
      upd({
        bio_short: data.bio_short || form.bio_short,
        bio_long: data.bio_long || form.bio_long,
        journey_text: data.journey_text || null,
        achievements: data.achievements || [],
        results: data.results || [],
        badges_suggested: data.badges_suggested || [],
        top_titles: data.top_titles || [],
        highlights: data.highlights || [],
        flash_recap: data.flash_recap || null,
        discipline_stats: data.discipline_stats || [],
        period_start: data.period_start ?? null,
        period_end: data.period_end ?? null,
        proposed_status: data.proposed_status || null,
        proposed_level: data.proposed_level || null,
        proposed_level_justification: data.proposed_level_justification || null,
        articles: data.articles || [],
        clubs: data.clubs || [],
        federations: data.federations || [],
        sources: data.sources || [],
        confidence: data.confidence ?? null,
        main_discipline: form.main_discipline || data.main_discipline || "",
        disciplines: form.disciplines.length ? form.disciplines : (data.disciplines || []).map((d) => d.toLowerCase()),
        commune: form.commune || data.commune || "",
        island: form.island || data.island || "",
        nickname: form.nickname || data.nickname || "",
        full_name: form.full_name || data.full_name || "",
      });
      toast.success("L'IA a préparé un brouillon — vérifie et publie.");
      setStep(reviewStep);
    } catch (e) {
      toast.error(`IA: ${normalizeError(e)}. Tu peux continuer manuellement.`);
      setStep(reviewStep);
    } finally {
      setLoadingAi(false);
    }
  };

  const publish = async () => {
    if (!user) {
      toast.error("Connecte-toi pour publier.");
      nav("/connexion?next=/creer-ma-fiche");
      return;
    }
    if (!form.consent_image_rights || !form.consent_data_accuracy) {
      toast.error("Les consentements sont obligatoires.");
      return;
    }
    try {
      const { data } = await api.post("/auth/complete-profile", {
        entity_type: entityType,
        roles: isPerson ? roles : [],
        profile_data: {
          full_name: form.full_name,
          nickname: form.nickname || null,
          commune: form.commune || null,
          island: form.island || null,
          sex: form.sex || null,
          photo_url: form.photo_url || null,
          bio_short: form.bio_short || null,
          bio_long: form.bio_long || null,
          journey_text: form.journey_text || null,
          main_discipline: form.main_discipline || null,
          disciplines: form.disciplines,
          achievements: form.achievements,
          results: form.results,
          badges_suggested: form.badges_suggested,
          top_titles: form.top_titles,
          highlights: form.highlights,
          sport_level: form.sport_level || null,
          period_start: form.period_start,
          period_end: form.period_end,
          proposed_status: form.proposed_status,
          proposed_level: form.proposed_level,
          proposed_level_justification: form.proposed_level_justification,
          flash_recap: form.flash_recap,
          discipline_stats: form.discipline_stats,
          articles: form.articles,
          clubs: form.clubs,
          federations: form.federations,
          sources: form.sources,
          confidence: form.confidence,
        },
      });
      setCreatedEntity(data);
      setStep(999); // page de succès
    } catch (e) {
      showError(e, { prefix: "Erreur :" });
    }
  };

  // Progress %
  const pct = step === 999 ? 100 : Math.min(100, ((step + 1) / totalSteps) * 100);

  // === Rendu ===
  return (
    <div className="mx-auto w-full max-w-3xl px-4 sm:px-6 py-8">
      <div className="mb-6">
        <div className="flex items-center justify-between text-xs text-muted-foreground mb-2">
          <span data-testid="create-profile-wizard-step-title" className="font-display font-semibold text-foreground text-sm">
            {step === 999 ? "Ta création est en ligne" : `Étape ${step + 1} · ${stepTitle(step, entityType, needsDisciplines)}`}
          </span>
          <span className="font-mono">{Math.round(pct)}%</span>
        </div>
        <Progress value={pct} data-testid="create-profile-wizard-progress" />
        <p className="text-sm text-muted-foreground mt-2">{stepSubtitle(step, entityType, needsDisciplines)}</p>
      </div>

      <Card className="p-5 sm:p-6 min-h-[300px]">
        {/* Step 0 : Type d'entité */}
        {step === 0 && (
          <div className="space-y-3" data-testid="wizard-entity-type">
            <p className="text-sm text-muted-foreground">Que souhaites-tu créer ?</p>
            <div className="grid gap-3 sm:grid-cols-3">
              {ENTITY_TYPES.map((opt) => {
                const Icon = opt.icon;
                const active = entityType === opt.key;
                return (
                  <button
                    key={opt.key}
                    type="button"
                    onClick={() => setEntityType(opt.key)}
                    data-testid={`wizard-entity-${opt.key}`}
                    className={`text-left rounded-lg border p-4 transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-[hsl(var(--aito-lagoon))] ${
                      active ? "border-[hsl(var(--aito-lagoon))] bg-[hsl(var(--aito-lagoon))]/10" : "hover:bg-muted"
                    }`}
                  >
                    <Icon className={`h-6 w-6 mb-2 ${active ? "text-[hsl(var(--aito-lagoon))]" : "text-muted-foreground"}`} />
                    <div className="font-medium">{opt.title}</div>
                    <div className="text-xs text-muted-foreground mt-1">{opt.subtitle}</div>
                  </button>
                );
              })}
            </div>
            {isClubOrFed && (
              <div className="mt-4 rounded-lg border bg-secondary/40 p-3 text-sm space-y-1.5">
                <div>
                  <strong>Bon à savoir&nbsp;:</strong> les pages Club et Fédération peuvent être gérées
                  par plusieurs personnes. Après création, tu pourras inviter d'autres gestionnaires.
                </div>
                {entityType === "club" && (
                  <div className="text-xs text-muted-foreground">
                    Une affiliation à une Fédération est facultative et se gère plus tard depuis
                    l'espace du club.
                  </div>
                )}
                {entityType === "federation" && (
                  <div className="text-xs text-muted-foreground">
                    Une fédération peut exister sur AITO.PF sans être affiliée au COPF ; toute
                    affiliation institutionnelle éventuelle se gère séparément.
                  </div>
                )}
              </div>
            )}
          </div>
        )}

        {/* Step 1 : Rôles multi-select (Personne uniquement) */}
        {step === 1 && isPerson && (
          <div className="space-y-4" data-testid="wizard-roles">
            <p className="text-sm text-muted-foreground">
              Coche tous les rôles qui te correspondent. Ils sont <strong>cumulables</strong> — tu peux
              par exemple être athlète ET coach en même temps.
            </p>
            <div className="grid gap-2 sm:grid-cols-2">
              {ROLE_OPTIONS.map((opt) => {
                const Icon = opt.icon;
                const active = roles.includes(opt.key);
                return (
                  <label
                    key={opt.key}
                    data-testid={`wizard-role-${opt.key}`}
                    className={`flex items-start gap-3 rounded-lg border p-3 cursor-pointer transition-colors ${
                      active ? "border-[hsl(var(--aito-lagoon))] bg-[hsl(var(--aito-lagoon))]/10" : "hover:bg-muted"
                    }`}
                  >
                    <Checkbox checked={active} onCheckedChange={() => toggleRole(opt.key)} data-testid={`wizard-role-checkbox-${opt.key}`} />
                    <div className="min-w-0">
                      <div className="font-medium flex items-center gap-2">
                        <Icon className="h-4 w-4 text-muted-foreground" />
                        {opt.label}
                      </div>
                      <div className="text-xs text-muted-foreground mt-0.5">{opt.desc}</div>
                    </div>
                  </label>
                );
              })}
            </div>
            {roles.length === 0 && (
              <p className="text-xs text-amber-700">Coche au moins un rôle pour continuer.</p>
            )}
          </div>
        )}

        {/* Step 2 : Identité */}
        {((isPerson && step === 2) || (isClubOrFed && step === 1)) && (
          <div className="space-y-4">
            <div>
              <Label className="text-xs font-medium">
                {isPerson ? "Nom complet" : entityType === "club" ? "Nom du club" : "Nom de la fédération"}
              </Label>
              <Input data-testid="wizard-name-input" value={form.full_name}
                onChange={(e) => upd({ full_name: e.target.value })}
                placeholder={isPerson ? "Ton nom complet" : "Nom officiel"} />
            </div>
            {isPerson && (
              <div className="grid sm:grid-cols-2 gap-3">
                <div>
                  <Label className="text-xs font-medium">Surnom (optionnel)</Label>
                  <Input data-testid="wizard-nickname-input" value={form.nickname}
                    onChange={(e) => upd({ nickname: e.target.value })} placeholder="Ton surnom" />
                </div>
                <div>
                  <Label className="text-xs font-medium">Sexe (pour visuel par défaut)</Label>
                  <select data-testid="wizard-sex-input" value={form.sex || ""}
                    onChange={(e) => upd({ sex: e.target.value })}
                    className="w-full h-10 rounded-md border border-input bg-background px-3 text-sm">
                    <option value="">— Non renseigné —</option>
                    <option value="homme">Homme</option>
                    <option value="femme">Femme</option>
                    <option value="autre">Autre / neutre</option>
                  </select>
                </div>
              </div>
            )}
            {isPerson && (
              <div className="grid sm:grid-cols-2 gap-3">
                <div className="sm:col-span-2">
                  <Label className="text-xs font-medium">Photo URL (optionnel)</Label>
                  <Input data-testid="wizard-photo-input" value={form.photo_url}
                    onChange={(e) => upd({ photo_url: e.target.value })} placeholder="https://…" />
                </div>
              </div>
            )}
            <div className="grid sm:grid-cols-2 gap-3">
              <div>
                <Label className="text-xs font-medium">Commune</Label>
                <Input data-testid="wizard-commune-input" value={form.commune}
                  onChange={(e) => upd({ commune: e.target.value })} placeholder="Commune" />
              </div>
              <div>
                <Label className="text-xs font-medium">Île</Label>
                <Input data-testid="wizard-island-input" value={form.island}
                  onChange={(e) => upd({ island: e.target.value })} placeholder="Île" />
              </div>
            </div>
            {isPerson && (
              <div>
                <Label className="text-xs font-medium">Niveau sportif (optionnel)</Label>
                <select data-testid="wizard-sport-level"
                  className="w-full mt-1 border rounded-md px-3 py-2 text-sm bg-background"
                  value={form.sport_level}
                  onChange={(e) => upd({ sport_level: e.target.value })}>
                  {SPORT_LEVEL_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
                </select>
              </div>
            )}
            {isPerson && roles.length > 0 && (
              <div className="text-xs text-muted-foreground">
                Rôles sélectionnés :{" "}
                {roles.map((r) => (
                  <Badge key={r} variant="secondary" className="mr-1">
                    {ROLE_OPTIONS.find((o) => o.key === r)?.label || r}
                  </Badge>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Step 3 : Disciplines (person + athlete/coach) */}
        {isPerson && step === 3 && needsDisciplines && (
          <div>
            <p className="text-sm text-muted-foreground mb-3">
              Choisis ta ou tes disciplines. La première sélectionnée devient la discipline principale.
            </p>
            <div className="flex flex-wrap gap-2" data-testid="wizard-disciplines">
              {disciplines.map((d) => {
                const active = form.disciplines.includes(d.slug);
                return (
                  <button key={d.slug} type="button"
                    data-testid={`wizard-discipline-${d.slug}`}
                    onClick={() => toggleDiscipline(d.slug)}
                    className={`px-3 py-1.5 rounded-full text-sm border transition-colors ${
                      active ? "bg-[hsl(var(--aito-lagoon))] text-black border-transparent" : "hover:bg-muted"
                    }`}>
                    {d.name}
                  </button>
                );
              })}
              <WizardOtherDiscipline form={form} upd={upd} />
            </div>
            {form.disciplines.length > 0 && (
              <p className="mt-3 text-xs text-muted-foreground">
                Discipline principale : <strong>{form.main_discipline}</strong>
              </p>
            )}
          </div>
        )}

        {/* Step Sources (URLs) */}
        {((isPerson && step === (needsDisciplines ? 4 : 3)) || (isClubOrFed && step === 2)) && (
          <div className="space-y-3">
            <div className="rounded-lg border bg-secondary/40 p-3">
              <p className="text-sm">
                <strong>Ajoute tes URLs officielles et sources publiques.</strong>
                {" "}Ce n'est pas obligatoire, mais chaque lien enrichit ton profil automatiquement.
              </p>
              <p className="text-xs text-muted-foreground mt-1.5">
                Types d'URLs acceptés (l'IA sait extraire articles <em>et</em> résultats officiels) :
              </p>
              <ul className="text-xs text-muted-foreground mt-1 ml-4 space-y-0.5 list-disc">
                <li><strong>Fiches officielles de fédération</strong> — internationale, nationale ou locale
                  <span className="text-[10px] italic"> (ex. IBJJF, ISA/WSL, World Athletics, IJF, UCI, FIVB, fédération française, ligue régionale…)</span>
                </li>
                <li><strong>Pages de résultats / classements</strong> de compétitions (l'IA lit chaque ligne de palmarès)</li>
                <li><strong>Articles de presse</strong> (Tahiti Infos, la 1ère, Tahiti Sports, presse spécialisée…)</li>
                <li><strong>Profils sportifs</strong> sur plateformes (ADCC, FloGrappling, athlete.world, etc.)</li>
              </ul>
              <p className="text-[11px] text-muted-foreground mt-2 italic">
                Astuce : plus les URLs pointent vers ta fiche officielle sur un site fédéral,
                mieux l'IA reconstitue ton palmarès complet.
              </p>
            </div>
            {form.urls.map((u, i) => (
              <div key={i} className="flex gap-2">
                <Input data-testid={`wizard-url-input-${i}`} value={u}
                  onChange={(e) => { const cp = [...form.urls]; cp[i] = e.target.value; upd({ urls: cp }); }}
                  placeholder="https://… (fiche fédération, article, résultats officiels…)" />
                <Button variant="ghost" size="icon" onClick={() => upd({ urls: form.urls.filter((_, j) => j !== i) })}>
                  <Trash2 className="h-4 w-4" />
                </Button>
              </div>
            ))}
            <div className="flex flex-wrap gap-2">
              <Button variant="outline" size="sm" onClick={() => upd({ urls: [...form.urls, ""] })}>
                <Plus className="h-4 w-4 mr-1" />Ajouter un lien
              </Button>
              {/* V11.2 — Recherche IA sans URL : propose des sources publiques à partir
                  de l'identité déjà saisie (prénom, nom, surnom, discipline, club). */}
              <AISourceSearchButton
                entity={{
                  first_name: (form.full_name || "").split(" ")[0],
                  last_name: (form.full_name || "").split(" ").slice(1).join(" "),
                  nickname: form.nickname,
                  discipline: form.main_discipline || (form.disciplines || [])[0],
                  club: undefined,
                  entity_type: entityType === "person" ? "athlete" : entityType,
                }}
                onAddSource={(cand) => {
                  const urls = (form.urls || []).filter((u) => u && u.trim());
                  if (urls.includes(cand.url)) {
                    toast.info("Source déjà présente.");
                    return;
                  }
                  urls.push(cand.url);
                  upd({ urls: urls.length ? urls : [""] });
                }}
                label="Recherche IA (sans URL)"
                variant="outline"
                size="sm"
                dataTestId="wizard-ai-find-sources-btn"
              />
              <Button variant="ghost" size="sm" onClick={skipAi} className="text-muted-foreground">
                <SkipForward className="h-4 w-4 mr-1" />Passer cette étape
              </Button>
            </div>
          </div>
        )}

        {/* Step IA */}
        {((isPerson && step === (needsDisciplines ? 5 : 4)) || (isClubOrFed && step === 3)) && (
          <div className="flex flex-col items-center justify-center py-10 text-center">
            {loadingAi ? (
              <>
                <Loader2 className="h-8 w-8 animate-spin text-[hsl(var(--aito-lagoon))]" />
                <p className="mt-3 text-sm text-muted-foreground">L'IA analyse tes liens…</p>
              </>
            ) : form.urls.filter(Boolean).length === 0 ? (
              <>
                <ListChecks className="h-8 w-8 text-[hsl(var(--aito-lagoon))]" />
                <h3 className="mt-3 font-display text-xl font-bold">Pas de lien — pas de souci.</h3>
                <p className="mt-1 text-sm text-muted-foreground max-w-md">
                  Continue manuellement. Tu pourras enrichir plus tard.
                </p>
                <Button data-testid="wizard-continue-manual" onClick={skipAi}
                  className="mt-5 bg-[hsl(var(--aito-lagoon))] text-black hover:bg-[hsl(var(--aito-lagoon-2))]">
                  Continuer manuellement <ArrowRight className="h-4 w-4 ml-1.5" />
                </Button>
              </>
            ) : (
              <>
                <Sparkles className="h-8 w-8 text-[hsl(var(--aito-lagoon))]" />
                <h3 className="mt-3 font-display text-xl font-bold">Prêt ?</h3>
                <p className="mt-1 text-sm text-muted-foreground max-w-md">
                  L'IA va analyser tes {form.urls.filter(Boolean).length} lien(s) pour pré-remplir un brouillon.
                </p>
                <div className="mt-5 flex gap-2 flex-wrap justify-center">
                  <Button data-testid="wizard-run-ai" onClick={runAi}
                    className="bg-[hsl(var(--aito-lagoon))] text-black hover:bg-[hsl(var(--aito-lagoon-2))]">
                    <Sparkles className="h-4 w-4 mr-1.5" />Lancer l'IA
                  </Button>
                  <Button variant="ghost" data-testid="wizard-skip-ai" onClick={skipAi}>
                    Passer l'IA
                  </Button>
                </div>
              </>
            )}
          </div>
        )}

        {/* Step Review */}
        {((isPerson && step === (needsDisciplines ? 6 : 5)) || (isClubOrFed && step === 4)) && (
          <div className="space-y-4" data-testid="wizard-review">
            {/* V9.21 Hotfix — le contenu de la revue est spécifique au type
                d'entité afin de ne plus mélanger champs Athlete et Club/Fédération. */}
            {isPerson ? (
              <>
                <div>
                  <Label className="text-xs font-medium">Bio courte</Label>
                  <Textarea data-testid="wizard-bio-short" rows={2} value={form.bio_short}
                    onChange={(e) => upd({ bio_short: e.target.value })}
                    placeholder="Quelques mots (1-2 phrases)" />
                </div>
                <div>
                  <Label className="text-xs font-medium">Bio longue (optionnel)</Label>
                  <Textarea data-testid="wizard-bio-long" rows={5} value={form.bio_long}
                    onChange={(e) => upd({ bio_long: e.target.value })}
                    placeholder="Parcours, objectifs, motivation…" />
                </div>
                {form.achievements.length > 0 && (
                  <div>
                    <div className="text-xs font-medium mb-2">Palmarès ({form.achievements.length})</div>
                    <div className="grid sm:grid-cols-2 gap-2">
                      {form.achievements.map((a, i) => <MedalCard key={i} {...a} />)}
                    </div>
                  </div>
                )}
              </>
            ) : (
              <>
                <div className="rounded-lg border bg-secondary/40 p-3 text-sm">
                  <div className="font-medium mb-1">
                    {entityType === "club" ? "Ta page Club est prête à être créée" : "Ta page Fédération est prête à être créée"}
                  </div>
                  <div className="text-xs text-muted-foreground">
                    Tu pourras enrichir la page (disciplines, coordonnées, affiliations éventuelles,
                    gestionnaires) depuis l'espace de gestion après création.
                  </div>
                </div>
                <div className="grid gap-2 text-sm">
                  <div className="flex items-center justify-between">
                    <span className="text-muted-foreground">Nom</span>
                    <span className="font-medium">{form.full_name || "—"}</span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-muted-foreground">Commune</span>
                    <span>{form.commune || "—"}</span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-muted-foreground">Île</span>
                    <span>{form.island || "—"}</span>
                  </div>
                </div>
                <div>
                  <Label className="text-xs font-medium">Présentation (optionnel)</Label>
                  <Textarea data-testid="wizard-bio-short" rows={3} value={form.bio_short}
                    onChange={(e) => upd({ bio_short: e.target.value })}
                    placeholder={entityType === "club"
                      ? "Une phrase décrivant ton club (discipline, esprit, valeurs…)."
                      : "Une phrase décrivant ta fédération (disciplines couvertes, missions…)."
                    } />
                </div>
              </>
            )}
            <div className="space-y-2 pt-2 border-t">
              <label className="flex items-start gap-2 text-xs">
                <Checkbox data-testid="wizard-consent-image" checked={form.consent_image_rights}
                  onCheckedChange={(v) => upd({ consent_image_rights: !!v })} />
                <span>Je confirme disposer des droits nécessaires pour publier les contenus.</span>
              </label>
              <label className="flex items-start gap-2 text-xs">
                <Checkbox data-testid="wizard-consent-accuracy" checked={form.consent_data_accuracy}
                  onCheckedChange={(v) => upd({ consent_data_accuracy: !!v })} />
                <span>Je confirme l'exactitude des informations.</span>
              </label>
            </div>
          </div>
        )}

        {/* Step Success */}
        {step === 999 && createdEntity && (
          <div className="text-center py-8" data-testid="create-profile-wizard-success">
            <Sparkles className="h-10 w-10 mx-auto text-[hsl(var(--aito-gold))]" />
            <h2 className="font-display text-2xl font-bold mt-3">C'est en ligne !</h2>
            <p className="mt-2 text-sm text-muted-foreground max-w-md mx-auto">
              {isPerson && `Bienvenue sur AITO.PF. ${createdEntity.created?.length > 1 ? "Tes fiches ont été créées" : "Ta fiche a été créée"}.`}
              {entityType === "club" && "Ton club est créé. Tu peux dès à présent le gérer, inviter des gestionnaires et compléter ses informations."}
              {entityType === "federation" && "Ta fédération est créée. Tu peux dès à présent la gérer, inviter des gestionnaires et compléter ses informations."}
            </p>
            <div className="mt-5 flex gap-2 justify-center flex-wrap">
              {createdEntity.athlete_slug && (
                <Link to={`/athletes/${createdEntity.athlete_slug}`}>
                  <Button className="bg-[hsl(var(--aito-lagoon))] text-black">Voir ma fiche athlète</Button>
                </Link>
              )}
              {createdEntity.coach_slug && (
                <Link to={`/coachs/${createdEntity.coach_slug}`}>
                  <Button variant="outline">Voir ma fiche coach</Button>
                </Link>
              )}
              {createdEntity.club_slug && (
                <Link to={`/clubs/${createdEntity.club_slug}`}>
                  <Button className="bg-[hsl(var(--aito-lagoon))] text-black" data-testid="success-view-club">Voir mon club</Button>
                </Link>
              )}
              {createdEntity.federation_slug && (
                <Link to={`/federations/${createdEntity.federation_slug}`}>
                  <Button className="bg-[hsl(var(--aito-lagoon))] text-black" data-testid="success-view-federation">Voir ma fédération</Button>
                </Link>
              )}
              <Link to="/dashboard">
                <Button variant="outline" data-testid="success-go-dashboard">Aller à mon espace</Button>
              </Link>
            </div>
          </div>
        )}
      </Card>

      <div className="mt-5 flex justify-between gap-3">
        <Button variant="outline" onClick={back}
          disabled={step === 0 || loadingAi || step === 999}
          data-testid="create-profile-wizard-back-button">
          <ArrowLeft className="h-4 w-4 mr-1" />Retour
        </Button>
        {step !== 999 && (
          <NextOrPublishButton
            step={step}
            entityType={entityType}
            roles={roles}
            isPerson={isPerson}
            needsDisciplines={needsDisciplines}
            form={form}
            loadingAi={loadingAi}
            onNext={next}
            onRunAi={runAi}
            onSkipAi={skipAi}
            onPublish={publish}
          />
        )}
      </div>
    </div>
  );
}

// -------------------------------------------------------------------------
// Helpers UI
// -------------------------------------------------------------------------
function stepTitle(step, entityType, needsDisc) {
  if (step === 999) return "Publication terminée";
  if (step === 0) return "Quel type ?";
  if (entityType === "person") {
    if (step === 1) return "Tes rôles";
    if (step === 2) return "Qui es-tu ?";
    if (step === 3 && needsDisc) return "Tes disciplines";
    if ((needsDisc && step === 4) || (!needsDisc && step === 3)) return "Sources (optionnel)";
    if ((needsDisc && step === 5) || (!needsDisc && step === 4)) return "Préparation IA";
    if ((needsDisc && step === 6) || (!needsDisc && step === 5)) return "Revue & publication";
  } else {
    if (step === 1) return `Nom du ${entityType === "club" ? "club" : "fédération"}`;
    if (step === 2) return "Sources (optionnel)";
    if (step === 3) return "Préparation IA";
    if (step === 4) return "Revue & publication";
  }
  return "";
}

function stepSubtitle(step, entityType, needsDisc) {
  if (step === 999) return "Ta création est visible et pourra être enrichie à tout moment.";
  if (step === 0) return "Choisis parmi Personne, Club, Fédération.";
  if (entityType === "person") {
    if (step === 1) return "Coche tous les rôles qui te correspondent (cumulables).";
    if (step === 2) return "Nom, commune, île — le tour est joué.";
    if (step === 3 && needsDisc) return "Choisis tes disciplines. Cumulables.";
    if ((needsDisc && step === 4) || (!needsDisc && step === 3)) return "Liens facultatifs (articles, résultats, sites officiels).";
    if ((needsDisc && step === 5) || (!needsDisc && step === 4)) return "Avec ou sans IA — au choix.";
    if ((needsDisc && step === 6) || (!needsDisc && step === 5)) return "Vérifie, coche les consentements et publie.";
  } else {
    if (step === 1) return "Informations minimales pour la page.";
    if (step === 2) return "Liens facultatifs (site officiel, articles, communiqués).";
    if (step === 3) return "Avec ou sans IA — au choix.";
    if (step === 4) return entityType === "club"
      ? "Vérifie les informations du club, coche les consentements et crée la page."
      : "Vérifie les informations de la fédération, coche les consentements et crée la page.";
  }
  return "";
}

function NextOrPublishButton({
  step, entityType, roles, isPerson, needsDisciplines,
  form, loadingAi, onNext, onRunAi, onSkipAi, onPublish,
}) {
  // Étape 0
  if (step === 0) {
    return (
      <Button onClick={onNext} disabled={!entityType}
        className="bg-[hsl(var(--aito-lagoon))] text-black hover:bg-[hsl(var(--aito-lagoon-2))]"
        data-testid="create-profile-wizard-next-button">
        Suivant <ArrowRight className="h-4 w-4 ml-1" />
      </Button>
    );
  }
  // Personne
  if (isPerson) {
    if (step === 1) {
      return (
        <Button onClick={onNext} disabled={roles.length === 0}
          className="bg-[hsl(var(--aito-lagoon))] text-black hover:bg-[hsl(var(--aito-lagoon-2))]"
          data-testid="create-profile-wizard-next-button">
          Suivant <ArrowRight className="h-4 w-4 ml-1" />
        </Button>
      );
    }
    if (step === 2) {
      return (
        <Button onClick={onNext} disabled={!form.full_name}
          className="bg-[hsl(var(--aito-lagoon))] text-black hover:bg-[hsl(var(--aito-lagoon-2))]"
          data-testid="create-profile-wizard-next-button">
          Suivant <ArrowRight className="h-4 w-4 ml-1" />
        </Button>
      );
    }
    if (step === 3 && needsDisciplines) {
      return (
        <Button onClick={onNext} disabled={form.disciplines.length === 0}
          className="bg-[hsl(var(--aito-lagoon))] text-black hover:bg-[hsl(var(--aito-lagoon-2))]"
          data-testid="create-profile-wizard-next-button">
          Suivant <ArrowRight className="h-4 w-4 ml-1" />
        </Button>
      );
    }
    // Étape sources
    if ((needsDisciplines && step === 4) || (!needsDisciplines && step === 3)) {
      return (
        <Button onClick={onNext}
          className="bg-[hsl(var(--aito-lagoon))] text-black hover:bg-[hsl(var(--aito-lagoon-2))]"
          data-testid="create-profile-wizard-next-button">
          Suivant <ArrowRight className="h-4 w-4 ml-1" />
        </Button>
      );
    }
    // Étape IA
    if ((needsDisciplines && step === 5) || (!needsDisciplines && step === 4)) {
      if (loadingAi) return null;
      return form.urls.filter(Boolean).length === 0 ? (
        <Button onClick={onSkipAi} data-testid="create-profile-wizard-ai-button"
          className="bg-[hsl(var(--aito-lagoon))] text-black">
          Continuer manuellement <ArrowRight className="h-4 w-4 ml-1" />
        </Button>
      ) : (
        <Button onClick={onRunAi} data-testid="create-profile-wizard-ai-button"
          className="bg-[hsl(var(--aito-lagoon))] text-black">
          <Sparkles className="h-4 w-4 mr-1.5" />Lancer l'IA
        </Button>
      );
    }
    // Étape publish
    if ((needsDisciplines && step === 6) || (!needsDisciplines && step === 5)) {
      return (
        <Button onClick={onPublish}
          className="bg-[hsl(var(--aito-gold))] text-black hover:bg-[hsl(43_74%_45%)]"
          data-testid="create-profile-wizard-publish-button">
          Créer mon profil
        </Button>
      );
    }
  }
  // Club/Fédération
  if (step === 1) {
    return (
      <Button onClick={onNext} disabled={!form.full_name}
        className="bg-[hsl(var(--aito-lagoon))] text-black"
        data-testid="create-profile-wizard-next-button">
        Suivant <ArrowRight className="h-4 w-4 ml-1" />
      </Button>
    );
  }
  if (step === 2) {
    return (
      <Button onClick={onNext} className="bg-[hsl(var(--aito-lagoon))] text-black"
        data-testid="create-profile-wizard-next-button">
        Suivant <ArrowRight className="h-4 w-4 ml-1" />
      </Button>
    );
  }
  if (step === 3) {
    if (loadingAi) return null;
    return form.urls.filter(Boolean).length === 0 ? (
      <Button onClick={onSkipAi} className="bg-[hsl(var(--aito-lagoon))] text-black"
        data-testid="create-profile-wizard-ai-button">
        Continuer manuellement <ArrowRight className="h-4 w-4 ml-1" />
      </Button>
    ) : (
      <Button onClick={onRunAi} className="bg-[hsl(var(--aito-lagoon))] text-black"
        data-testid="create-profile-wizard-ai-button">
        <Sparkles className="h-4 w-4 mr-1.5" />Lancer l'IA
      </Button>
    );
  }
  if (step === 4) {
    const label = entityType === "club" ? "Créer le club" : entityType === "federation" ? "Créer la fédération" : "Publier";
    return (
      <Button onClick={onPublish} className="bg-[hsl(var(--aito-gold))] text-black"
        data-testid="create-profile-wizard-publish-button">
        {label}
      </Button>
    );
  }
  return null;
}
